// Status marks, adapted from design/isomorphic/src/core/status.tsx: the pill follows
// isomorphic-core's get-status-badge (thin border, tinted background), the dot form the
// Isomorphic tables' status column. Status colour always comes with a label, never alone.
// Teal (secondary) is reserved for "end-to-end encrypted".
import { LockKey } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../i18n";
import { cn } from "./cn";
import { statusLabel, type Tone } from "./tones";

export type { Tone };

const pill: Record<Tone, string> = {
  green: "border-green/30 bg-green-lighter text-green-dark",
  orange: "border-orange/30 bg-orange-lighter text-orange-dark",
  red: "border-red/30 bg-red-lighter text-red-dark",
  blue: "border-blue/30 bg-blue-lighter text-blue-dark",
  gray: "border-gray-300 bg-gray-100 text-gray-600",
  primary: "border-primary/30 bg-primary-lighter text-primary-dark",
  secondary: "border-secondary/30 bg-secondary-lighter text-secondary-dark",
};

const dot: Record<Tone, string> = {
  green: "bg-green",
  orange: "bg-orange",
  red: "bg-red",
  blue: "bg-blue",
  gray: "bg-gray-400",
  primary: "bg-primary",
  secondary: "bg-secondary",
};

export function Pill({ tone, children, className, icon }: { tone: Tone; children: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium tracking-wide [&>svg]:h-3.5 [&>svg]:w-3.5",
        pill[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

export function DotStatus({ tone, label, pulse }: { tone: Tone; label: ReactNode; pulse?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="relative flex h-2 w-2">
        {pulse ? <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-60", dot[tone])} /> : null}
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", dot[tone])} />
      </span>
      <span className="text-[13px] font-medium text-gray-800">{label}</span>
    </span>
  );
}

/** The mark for anything only the user's devices can read. */
export function E2EEBadge({ label, className }: { label?: string; className?: string }) {
  const t = useT();
  return (
    <Pill tone="secondary" className={className} icon={<LockKey weight="fill" />}>
      {label ?? t.common.e2ee}
    </Pill>
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("whitespace-nowrap font-mono text-[12.5px] text-gray-700", className)}>{children}</span>;
}

export function StatusPill({ tone, status }: { tone: Tone; status: string }) {
  // Re-render on a language change: statusLabel reads the active messages.
  useT();
  return (
    <Pill tone={tone}>
      <span className="inline-block first-letter:uppercase">{statusLabel(status)}</span>
    </Pill>
  );
}
